import { List } from 'immutable';

/**
 * Filters providers by transport type.
 * @param {Immutable.List} providers - List of providers to filter.
 * @param {string} showing - Transport type to show ('all' or specific type).
 * @returns {Immutable.List} Filtered providers.
 */
export const filterByTransport = (providers, showing) => {
  if (!providers) return List();
  if (showing === 'all') return providers;

  return providers.filter((provider) => showing === provider.get('transportType'));
};

/**
 * Filters providers to only those with at least one open trip.
 * @param {Immutable.List} providers - List of providers to filter.
 * @param {Immutable.List} trips - List of trips to check.
 * @returns {Immutable.List} Providers with open trips.
 */
export const filterByOpenProvider = (providers, trips) => {
  if (!providers) return List();
  if (!trips) return List();

  // The provider must have at least one open trip
  return providers.filter((provider) => {
    const providerTrips = trips.filter((trip) => trip.get('providerId') === provider.get('id'));
    return providerTrips.some((trip) => trip.get('openTicket'));
  });
};

/**
 * Returns true if a trip belongs to the given provider or transporter.
 * transporterName takes precedence over providerId when both are provided.
 * @param {Immutable.Map} trip - Trip to check.
 * @param {string} [providerId] - Provider id (lineId) to match.
 * @param {string} [transporterName] - Transporter name to match.
 * @returns {boolean}
 */
export const matchesTripProvider = (trip, providerId, transporterName) => {
  if (transporterName) return trip.getIn(['line', 'transporterName']) === transporterName;
  if (providerId) return trip.get('lineId') === providerId;
  return true;
};

/**
 * Filters providers to only show those belonging to the same transporter as the departure trip.
 * @param {Immutable.List} providers - List of providers to filter.
 * @param {string} transporterName - The transporter name to match.
 * @returns {Immutable.List} Filtered providers.
 */
export const filterByTransporter = (providers, transporterName) => {
  if (!providers) return List();
  if (!transporterName) return providers;
  return providers.filter((provider) => provider.get('transporterName') === transporterName);
};

/**
 * Resolves how return trips should be scoped relative to the selected departure on a round trip.
 *
 * When no provider id is present in the URL, the return leg has historically been scoped to the
 * departure line. This helper centralizes that inference and its two opt-in relaxations, applied
 * with the following precedence:
 *  1. `ALLOW_ANY_LINE_ROUND_TRIPS`  → no scope: any line covering the route is offered (marketplace).
 *  2. `ALLOW_CROSS_LINE_ROUND_TRIPS`→ scope to the departure transporter group (sibling lines).
 *  3. neither flag                  → scope to the exact departure line id.
 * When a provider id is already in the URL (explicit user selection), it is always respected and
 * no inference happens.
 *
 * @param {Object} params - Function params.
 * @param {Object} params.features - Whitelabel features.
 * @param {string} [params.providerId] - Provider id already present in the URL (explicit selection).
 * @param {Object} [params.departureTrip] - Selected departure trip (plain JS object).
 * @param {boolean} params.hasReturnDate - Whether the search is a round trip.
 * @param {boolean} [params.returnIsOpenTicket] - Whether the return is an open ticket.
 * @returns {{ providerId: (string|undefined), departureTransporterName: (string|undefined) }}
 */
export const resolveReturnTripScope = ({
  features,
  providerId,
  departureTrip,
  hasReturnDate,
  returnIsOpenTicket = false,
}) => {
  let resolvedProviderId = providerId;
  let departureTransporterName;

  const shouldInferScope =
    hasReturnDate && !resolvedProviderId && departureTrip && !returnIsOpenTicket;

  if (shouldInferScope) {
    const departureLineId = departureTrip.lineId || departureTrip.providerId;
    if (features.ALLOW_ANY_LINE_ROUND_TRIPS) {
      // Any line allowed on return: leave the scope open so every line on the route is offered.
    } else if (features.ALLOW_CROSS_LINE_ROUND_TRIPS) {
      // Same brand, multiple lines: filter returns by transporter instead of a single line id.
      departureTransporterName = departureTrip.line?.transporterName;
      // Without a transporter name, matchesTripProvider / filterTrips would not scope returns;
      // fall back to the departure line id (same as flag off).
      if (!departureTransporterName) {
        resolvedProviderId = departureLineId;
      }
    } else {
      resolvedProviderId = departureLineId;
    }
  }

  return { providerId: resolvedProviderId, departureTransporterName };
};

/**
 * Retrieves the cheapest open trip from a list of trips.
 * @param {Immutable.List} trips - List of trips.
 * @param {string} [providerId] - Provider id to filter by (optional).
 * @param {string} [transporterName] - Transporter name to filter by (optional).
 * @returns {Object|null} The cheapest open trip as JS object, or null if none found.
 */
export const retrieveOpenTrip = (trips, providerId, transporterName) => {
  if (!trips || trips.size === 0) return null;

  const openTrips = trips
    .filter((t) => t.get('openTicket') && matchesTripProvider(t, providerId, transporterName))
    .sort((a, b) => a.getIn(['pricing', 'total']) - b.getIn(['pricing', 'total']));

  return openTrips.size > 0 ? openTrips.first().toJS() : null;
};
